<template>
  <div>
    <ReviewAndSubmitComponent>
      <Alert v-if="this.showPremiumImpactAlert" :message="`Please note, your premium could change as a result of your updated details.`" />
      <div v-for="(section, index1) in summaryContent" :key="section.title">
        <FormField :helperText="section.infoText ? section.infoText : ''" noLabelOverride>
          <h3 class="review-subheading">{{ section.title }}</h3>
          <Alert v-for="(alert, index) in section.alerts" :key="index" :type="alert.type" :message="alert.message" />
          <table class="review-and-submit-table">
            <tr
              v-for="(item, index2) in section.values"
              :key="index2"
              v-bind:class="{
                item,
                update: item.diff === FormFieldUpdateStatus.UPDATED,
                remove: item.diff === FormFieldUpdateStatus.REMOVED,
              }"
            >
              <template v-if="(item.value !== '' && item.value !== undefined) || item.diff === FormFieldUpdateStatus.REMOVED">
                <template v-if="item.type && item.type === 'checkbox'">
                  <input role="cell" type="checkbox" :checked="item.value" disabled />
                  <label role="cell" class="checkDisabled" :aria-label="getAriaLabel(item.value, item.diff)">{{ item.title }}</label>
                </template>
                <template role="row" v-else class="section-row" :aria-rowindex="getRowIndex(index1, index2)">
                  <th role="cell" class="bold">{{ item.title }}</th>
                  <td v-html="item.value" role="cell" :aria-label="getAriaLabel(item.value, item.diff)" v-bind:class="{ bold: item.diff === FormFieldUpdateStatus.UPDATED }" />
                </template>
              </template>
            </tr>
          </table>
        </FormField>
      </div>
    </ReviewAndSubmitComponent>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { cloneDeep, isEmpty } from 'lodash';
import { VUEX_MEMBER_DETAILS } from '@/constants/VuexConstants';
import { format, parse, getYear, differenceInDays } from 'date-fns';
import { showConcessionCardDetailsForm, showConcessionCardSection, showVeteranCardSection } from '@/utils';
import {
  AcceptableThisEligibilityType,
  AccessLevel,
  ContactNumberType,
  EligibilityTypes,
  NonDependantFlexCodes,
  UpdateMemberDetailsStepNames,
  VeteranCardTypeOptions,
} from '@/enums';
import { ReviewAndSubmitComponent } from '@/components';
import { getDeclarationLabel, getServicePeriodLabel, isEmptyString, reverseFormatFormDate, toSentenceCase, under21Years11Month } from '@general/js/utils';
import { Eligibility, FormFieldUpdateStatus } from '@general/js/enums';
import MemberDetailsMixin from './MemberDetailsMixin';

export default {
  name: UpdateMemberDetailsStepNames.step8,
  mixins: [MemberDetailsMixin],
  props: {
    initialData: {
      type: Object,
    },
    // Boolean for if student declaration has triggered scale change
    scaleChange: {
      type: Boolean,
    },
  },
  components: {
    ReviewAndSubmitComponent,
  },
  data() {
    return {
      FormFieldUpdateStatus,
      showHasConcessionCardHelpText: false,
    };
  },
  beforeMount() {
    this.showHasConcessionCardHelpText = showConcessionCardDetailsForm(
      this.memberDetails?.memberInformation?.residentialAddress?.state,
      this.memberDetails?.memberInformation?.dateOfBirth,
      this.memberDetails?.membershipSummary?.extrasOnly,
      this.memberDetails?.concessionCard?.hasConcessionCard,
    );
  },
  methods: {
    ...mapMutations('MemberDetails', {
      updateHasFormBeenUpdated: VUEX_MEMBER_DETAILS.MUTATIONS.updateHasFormBeenUpdated,
    }),
    /**
     * Returns a diff object checking if the left value has been updated, removed or not changed
     * based on the comparison to the right object
     */
    diffUpdatedValues(left, right) {
      this.updateHasFormBeenUpdated(false);
      const diffObjects = (lhs, rhs) => {
        const diffResult = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const key of Object.keys(lhs)) {
          // Diff array values
          if (Array.isArray(lhs[key]) && Array.isArray(rhs[key])) {
            diffResult[key] = rhs[key].reduce((diff, _, index) => {
              // If we have more then initial lhs array then value must have been added
              if (index > lhs[key].length - 1) {
                diff.push(FormFieldUpdateStatus.UPDATED);
                this.updateHasFormBeenUpdated(true);
              } // Array value type is array or object
              else if (
                (Array.isArray(lhs[key][index]) && Array.isArray(rhs[key][index])) ||
                (typeof rhs[key][index] === 'object' && lhs[key][index] !== null && rhs[key][index] !== null)
              ) {
                diff.push(diffObjects(lhs[key][index], rhs[key][index]));
              } // Primitive value in array, so compare
              else if (lhs[key][index] !== rhs[key][index]) {
                diff.push(FormFieldUpdateStatus.UPDATED);
                this.updateHasFormBeenUpdated(true);
              } else {
                diff.push(FormFieldUpdateStatus.NO_CHANGE);
              }

              return diff;
            }, []);

            // If user has removed values, pad the result array with removed
            if (lhs[key].length > rhs[key].length) {
              diffResult[key] = diffResult[key].concat(Array(lhs[key].length - rhs[key].length).fill(FormFieldUpdateStatus.REMOVED));
            }
          } else if (lhs[key] == null && rhs[key] == null) {
            // Catch any scenarios where some permutation off null/undefined for both sides
            diffResult[key] = FormFieldUpdateStatus.NO_CHANGE;
          } else if (lhs[key] !== null && rhs[key] !== null && typeof rhs[key] === 'object') {
            // Diff object values
            diffResult[key] = diffObjects(lhs[key], rhs[key]);
          } else if (lhs[key] !== '' && rhs[key] === '') {
            // Removed
            diffResult[key] = FormFieldUpdateStatus.REMOVED;
            this.updateHasFormBeenUpdated(true);
          } else if (key in rhs === false) {
            // Removed
            diffResult[key] = FormFieldUpdateStatus.REMOVED;
            this.updateHasFormBeenUpdated(true);
          } else if (JSON.stringify(lhs[key])?.toLowerCase()?.replace(/\s/g, '') !== JSON.stringify(rhs[key])?.toLowerCase()?.replace(/\s/g, '')) {
            // Comparing data in string without any white space
            // Updated value
            diffResult[key] = FormFieldUpdateStatus.UPDATED;
            this.updateHasFormBeenUpdated(true);
          } else {
            // No change
            diffResult[key] = FormFieldUpdateStatus.NO_CHANGE;
          }
        }
        // Mark any new object key/value pairs added to rhs that are not in lhs as updated
        const lhsSideKeys = Object.keys(lhs);
        const rhsSideKeys = Object.keys(rhs);
        const newRhsKeys = rhsSideKeys.filter((rhsKey) => {
          return !lhsSideKeys.includes(rhsKey);
        });

        newRhsKeys.forEach((rhsKey) => {
          diffResult[rhsKey] = FormFieldUpdateStatus.UPDATED;
          this.updateHasFormBeenUpdated(true);
        });

        return diffResult;
      };
      const diff = diffObjects(left, right);
      return diff;
    },
    /**
     * Returns the combined diff result based on an array of given diff values
     */
    diffCombinedValues(values) {
      if (values.includes(FormFieldUpdateStatus.UPDATED)) {
        return FormFieldUpdateStatus.UPDATED;
      }
      if (
        values.every((v) => {
          return v === FormFieldUpdateStatus.REMOVED;
        })
      ) {
        return FormFieldUpdateStatus.REMOVED;
      }
      return FormFieldUpdateStatus.NO_CHANGE;
    },
    boolToYesNo(boolean) {
      return {
        true: 'Yes',
        false: 'No',
        undefined: '',
        null: '',
      }[boolean];
    },
    mapFormToSubmissionPayload() {
      // Reset contact values (In the case the user remmoved one this ensures it's removed)
      this.memberDetails.contactInformation.mobilePhoneNumber = '';
      this.memberDetails.contactInformation.homePhoneNumber = '';
      this.memberDetails.contactInformation.workPhoneNumber = '';

      this.memberDetails.contactInformation.preferredContactMethods.forEach((contact) => {
        if (contact.type.toLowerCase() === ContactNumberType.MOBILE) {
          this.memberDetails.contactInformation.mobilePhoneNumber = contact.number.toString();
        }
        if (contact.type.toLowerCase() === ContactNumberType.HOME) {
          this.memberDetails.contactInformation.homePhoneNumber = contact.number.toString();
        }
        if (contact.type.toLowerCase() === ContactNumberType.WORK) {
          this.memberDetails.contactInformation.workPhoneNumber = contact.number.toString();
        }
      });
    },
    getAriaLabel(value, diffStatus) {
      if (diffStatus === FormFieldUpdateStatus.NO_CHANGE) {
        return value;
      }
      return `${value} ${diffStatus?.toLowerCase()}`;
    },
    getRowIndex(index1, index2) {
      if (index1 === 0) {
        if (index2 !== 0) {
          return index2 + 1;
        }

        return 1;
      }
      return `${index1 + 1}${index2 + 1}`;
    },

    // adding space before each concession card
    getSpaceAfterCards(title) {
      return title && title.toLowerCase() === 'which concession card do you hold?';
    },

    /** create key-value pairs of "veteranCardWhiteOptions" from refData for "acceptedConditions" list
     * e.g. veteranCardWhiteOptions: {
     *        DVAC0: "Shoulder",
     *        DVAC1: "Ankle",
     *        DVAC2: "Alcohol Dependence & Abuse",
     *        ...
     *     }
     */
    filterVeteranCardWhiteOptions() {
      if (this.refData?.veteranCardWhiteOptions) {
        return this.refData.veteranCardWhiteOptions.reduce((obj, item) => {
          const pairValues = ((obj[item.returnValue] = item.display), obj);
          return pairValues;
        }, {});
      }
      return {};
    },

    getMemberInformationSectionData(memberDetailsDiff) {
      const memberDetails = this.memberDetails.memberInformation;

      const resAddr = memberDetailsDiff.residentialAddress;
      const isResidentialAddresDiff = this.diffCombinedValues([resAddr.addressLine1, resAddr.addressLine2, resAddr.addressLine3, resAddr.suburb, resAddr.postcode, resAddr.state]);

      let currentTitle = memberDetails.title;
      if (this.getCurrentTitle.length !== 0) {
        currentTitle = this.getCurrentTitle[0].display;
      }

      const memberInformationTile = {
        title: 'Member information',
        values: [
          { title: 'Title / Rank', value: currentTitle, diff: memberDetailsDiff.title },
          {
            title: 'First name',
            value: memberDetails.firstName,
            diff: memberDetailsDiff.firstName,
          },
          {
            title: 'Middle name',
            value: memberDetails.middleName,
            diff: memberDetailsDiff.middleName,
          },
          {
            title: 'Last name',
            value: memberDetails.lastName,
            diff: memberDetailsDiff.lastName,
          },
          {
            title: 'Preferred name',
            value: memberDetails.preferredName,
            diff: memberDetailsDiff.preferredName,
          },
          { title: 'Sex', value: memberDetails.sex, diff: memberDetailsDiff.sex },
          {
            title: 'Date of birth',
            value: format(new Date(memberDetails.dateOfBirth), 'dd / MM / yyyy'),
            diff: memberDetailsDiff.dateOfBirth,
          },
          {
            title: 'Covered on this membership as',
            value: undefined,
            diff: memberDetailsDiff.coveredOnMembershipAsCode,
          },
          {
            title: 'Residential address',
            value: this.getFullAddress('memberInformation.residentialAddress'),
            diff: isResidentialAddresDiff,
          },
        ],
      };

      return memberInformationTile;
    },

    getContactInformationSectionData(contactInformationDiff) {
      const contactInformation = this.memberDetails.contactInformation;
      const initialContactInformation = this.initialData.contactInformation;

      const postAddr = contactInformationDiff.postalAddress;
      const isPostalAddressDiff = this.diffCombinedValues([
        postAddr.addressLine1,
        postAddr.addressLine2,
        postAddr.addressLine3,
        postAddr.suburb,
        postAddr.postcode,
        postAddr.state,
      ]);

      const formatPhoneNumber = (number) => {
        const num = number.toString().replace(/\s/g, '');
        // '04' is reserved for regional code for mobile number in Australia
        if (num.startsWith('04')) {
          return `${num.slice(0, 4)} ${num.slice(4, 7)} ${num.slice(7)}`;
        }
        return `${num.slice(0, 2)} ${num.slice(2, 6)} ${num.slice(6)}`;
      };

      const contacts = [];
      ['mobilePhoneNumber', 'homePhoneNumber', 'workPhoneNumber'].forEach((key) => {
        const numberExist = !isEmptyString(contactInformation[key]);
        const numberRemoved = isEmptyString(contactInformation[key]) && !isEmptyString(initialContactInformation[key]);

        if (numberExist || numberRemoved) {
          contacts.push({
            title: key[0].toUpperCase() + key.slice(1).replace('PhoneNumber', ''),
            value: formatPhoneNumber(contactInformation[key]),
            diff: numberRemoved ? FormFieldUpdateStatus.REMOVED : contactInformationDiff[key],
          });
        }
      });

      let paymentAdvice = [];
      if (!this.isDependant) {
        paymentAdvice = [
          {
            title: 'When we pay a claim, would you like to receive a payment advice?',
            value: this.boolToYesNo(contactInformation.paymentAdvice),
            diff: contactInformationDiff.paymentAdvice,
          },
        ];
      }

      const contactInformationTile = {
        title: 'Contact information',
        values: [
          ...contacts,
          {
            title: 'Email address',
            value: contactInformation.email,
            diff: contactInformationDiff.email,
          },
          {
            title: 'Postal address',
            value: this.getFullAddress('contactInformation.postalAddress', contactInformation.postalAddress.isPOBox),
            diff: isPostalAddressDiff,
          },
          {
            title: 'Preferred communication method',
            value: this.mapPreferredCommunicationMethodDisplayText,
            diff: contactInformationDiff.preferredCommunicationMethod,
          },
          ...paymentAdvice,
        ],
      };

      return contactInformationTile;
    },

    getPrivacyAndAccessSectionData(privacyAccessDiff) {
      const privacyAccess = this.memberDetails.privacyAccess;
      const privacyValues = [];
      const accessLevelsNotToShowPolicyPermission = [AccessLevel.POLICY_HOLDER_ACCESS, AccessLevel.LIMITED_ACCESS, AccessLevel.NO_ACCESS];

      if (this.loggedInUserAccessingThemselves) {
        privacyValues.push({
          title: 'Keep my information private',
          value: this.boolToYesNo(privacyAccess.keepPrivate),
          diff: privacyAccessDiff.keepPrivate,
        });
      }

      if (!accessLevelsNotToShowPolicyPermission.includes(privacyAccess.policyPermission.toLowerCase())) {
        privacyValues.push({
          title: 'Policy permission',
          value: toSentenceCase(privacyAccess.policyPermission),
          diff: privacyAccessDiff.policyPermission,
        });
      }

      if ([AccessLevel.POLICY_HOLDER_ACCESS, AccessLevel.LIMITED_ACCESS].includes(privacyAccess.policyPermission.toLowerCase())) {
        privacyValues.push({
          title: 'Policy permission',
          value: toSentenceCase(privacyAccess.policyPermission),
          diff: privacyAccessDiff.policyPermission,
        });
      }

      const privacyAndAccessTile = {
        title: 'Privacy and access',
        values: privacyValues,
      };
      return privacyAndAccessTile;
    },

    getStudentDeclarationSectionData(studentDeclarationDiff) {
      const underAge = under21Years11Month(this.memberDetails?.memberInformation?.dateOfBirth);
      const isDependantWithinStudentAge =
        this.isDependant &&
        this?.memberDetails?.memberInformation?.dateOfBirth &&
        this.withinStudentAge(this.reverseFormatFormDate(this.memberDetails.memberInformation.dateOfBirth));
      let studentDeclarationTile = {};

      if (!underAge && isDependantWithinStudentAge) {
        const studentDeclaration = this.memberDetails.studentDeclaration;
        const instituteDiff =
          this.initialData?.studentDeclaration?.instituteName !== studentDeclaration?.instituteName ? FormFieldUpdateStatus.UPDATED : FormFieldUpdateStatus.NO_CHANGE;

        let startDate = '';
        let isStudent = false;
        let isStudentUpdated = FormFieldUpdateStatus.NO_CHANGE;
        let isStudentShowInfoAlert = false;
        let yearOfStudyValue = '';
        if (studentDeclaration !== null && studentDeclaration.isStudent) {
          isStudent = true;
          startDate = parse(studentDeclaration.startDate, 'yyyy-MM-dd', new Date());

          if (this.initialData.studentDeclaration === null) {
            isStudentUpdated = FormFieldUpdateStatus.UPDATED;
          }

          try {
            yearOfStudyValue = getYear(startDate);
          } catch (error) {
            logger.log(error);
          }
        } else {
          // Yes/None -> No
          isStudentUpdated = this.memberDetails.studentDeclaration !== null ? FormFieldUpdateStatus.UPDATED : FormFieldUpdateStatus.NO_CHANGE;
          // Yes -> No
          isStudentShowInfoAlert = !!(this.initialData.studentDeclaration !== null && this.memberDetails.studentDeclaration !== null);
        }

        const yearOfStudyDiff = this.diffCombinedValues([studentDeclarationDiff.startDate, studentDeclarationDiff.endDate]);

        studentDeclarationTile = {
          title: 'Student declaration',
          values: [
            {
              title: 'Are you a student?',
              value: this.boolToYesNo(isStudent),
              diff: isStudentUpdated,
            },
            {
              title: 'Name of the school, college, university or TAFE at which you are studying',
              value: studentDeclaration?.instituteName,
              diff: studentDeclarationDiff.instituteName ?? instituteDiff,
            },
            { title: 'Year of study', value: yearOfStudyValue, diff: yearOfStudyDiff },
          ],
          alerts: [],
        };

        if (
          isStudent === false &&
          isStudentUpdated === FormFieldUpdateStatus.UPDATED &&
          isStudentShowInfoAlert === true &&
          (this.currentAccessLevel === AccessLevel.POLICY_HOLDER_ACCESS || this.currentAccessLevel === AccessLevel.FULL_ACCESS)
        ) {
          studentDeclarationTile.alerts.push({
            type: 'info',
            message: 'You have changed your dependant from a student dependant to a non-student dependant, you may be required to pay extended family premiums on your policy.',
          });
        }

        // only add student declaration checkbox on tile if is student
        if (isStudent === true) {
          studentDeclarationTile.values.push({
            title: 'I declare the details that I have provided are true and correct.',
            value: true,
            type: 'checkbox',
          });
        }
      }

      return studentDeclarationTile;
    },

    getEligibilityInformationSectionData(eligibilityInformationDiff) {
      const eligibilityInformation = this.memberDetails.eligibility;

      if (eligibilityInformation === null) {
        return {};
      }

      const accessingMemberAccessLevel = this.accessLevel && this.accessLevel(this.$route.params.policyNumber); // access level of current logged in member who is accessing member details
      const thisMemberDetailsIsPolicyHolder = this.memberDetails?.memberInformation?.coveredOnMembershipAsCode === NonDependantFlexCodes.POLICY_HOLDER; // check if member details is for policy holder or not
      const showEligibilityInformation =
        (accessingMemberAccessLevel === AccessLevel.POLICY_HOLDER_ACCESS || accessingMemberAccessLevel === AccessLevel.FULL_ACCESS) && thisMemberDetailsIsPolicyHolder;
      let eligibilityInformationTile = {};

      if (showEligibilityInformation) {
        const eligibilityValues = [];

        let eligibilityObject = {};

        // these three values are common between eligibilty types, so we add all three for all types
        eligibilityObject = {
          eligibilityType: !isEmptyString(this.iCanJoinDisplayText)
            ? {
                display: 'I can join Defence Health because',
                value: this.iCanJoinDisplayText,
              }
            : null,
          eligibilitySubject: !isEmptyString(eligibilityInformation.eligibilitySubject)
            ? {
                display: this.eligibilitySubjectLabel,
                value: toSentenceCase(eligibilityInformation.eligibilitySubject),
              }
            : null,
          subjectName:
            !isEmptyString(eligibilityInformation.eligibilitySubject) && eligibilityInformation.eligibilitySubject.toLowerCase() === 'partner'
              ? {
                  display: this.thisEligibilityType === AcceptableThisEligibilityType.EX_SERVING ? "Veteran's name" : "Partner's name",
                  value: eligibilityInformation.subjectName,
                }
              : null,
        };

        // create a display/value object for eligibility summary
        switch (this.thisEligibilityType) {
          case AcceptableThisEligibilityType.SERVING:
            eligibilityObject = {
              ...eligibilityObject,
              subjectRank: { display: 'Rank', value: eligibilityInformation.subjectRank },
              subjectDepartmentOrCompanyName: {
                display: `I/My partner serve ${this.isServing ? '' : 'd'} in the`,
                value: eligibilityInformation.subjectDepartmentOrCompanyName,
              },
              subjectServiceType: {
                display: getServicePeriodLabel(Eligibility.SERVING),
                value: eligibilityInformation.subjectServiceType,
              },
              enlistmentDate: {
                display: 'Date of enlistment',
                value: reverseFormatFormDate(eligibilityInformation.enlistmentDate),
              },
              sercatStatus: {
                display: 'SERCAT status - 3, 4, 5, 6 or 7',
                value: `SERCAT ${eligibilityInformation.sercatStatus}`,
              },
              sercatFileNames:
                eligibilityInformation.sercatFileNames && eligibilityInformation.sercatFileNames.length > 0
                  ? {
                      display: 'Eligibility evidence',
                      value: eligibilityInformation.sercatFileNames.join('<br/>'),
                    }
                  : null,
              declarationDate: {
                display: 'Eligibility declaration date',
                value: reverseFormatFormDate(eligibilityInformation.declarationDate),
              },
              expiryDate: {
                display: 'ADF declaration expiry date',
                value: reverseFormatFormDate(eligibilityInformation.expiryDate),
              },
            };
            break;
          case AcceptableThisEligibilityType.EX_SERVING:
            eligibilityObject = {
              ...eligibilityObject,
              subjectRank: { display: 'Rank', value: eligibilityInformation.subjectRank },
              subjectDepartmentOrCompanyName: {
                display: `I/My partner serve${this.isServing ? '' : 'd'} in the`,
                value: eligibilityInformation.subjectDepartmentOrCompanyName,
              },
              subjectServiceType: {
                display: getServicePeriodLabel(Eligibility.EX_SERVING),
                value: eligibilityInformation.subjectServiceType,
              },
              enlistmentDate: {
                display: 'Date of enlistment',
                value: reverseFormatFormDate(eligibilityInformation.enlistmentDate),
              },
              dischargeDate: {
                display: 'Date of discharge',
                value: reverseFormatFormDate(eligibilityInformation.dischargeDate),
              },
            };
            break;
          case AcceptableThisEligibilityType.RELATED:
            eligibilityObject = {
              ...eligibilityObject,
              subjectRelation: {
                display: 'The person who serves/served is my',
                value: eligibilityInformation.subjectRelation,
              },
              workConnectionName: {
                display: 'Name of connected individual',
                value: eligibilityInformation.workConnectionName,
              },
              subjectEligibilityType: !isEmptyString(eligibilityInformation.subjectEligibilityType)
                ? {
                    display: 'How are they eligible?',
                    value: eligibilityInformation.subjectEligibilityType,
                  }
                : null,
              subjectRank: { display: 'Rank', value: eligibilityInformation.subjectRank },
              subjectDepartmentOrCompanyName: {
                display: this.isContractorOrDefencePublicService
                  ? `${eligibilityInformation.subjectEligibilityType.toLowerCase() === 'contractor' ? 'Company' : 'Department'} name`
                  : `They serve${eligibilityInformation.subjectEligibilityType === 'Ex-serving' ? 'd' : ''} in the`,
                value: eligibilityInformation.subjectDepartmentOrCompanyName,
              },
              subjectServiceType:
                !isEmptyString(eligibilityInformation.subjectServiceType) &&
                (EligibilityTypes.SERVING.includes(eligibilityInformation.subjectEligibilityType) ||
                  EligibilityTypes.EX_SERVING.includes(eligibilityInformation.subjectEligibilityType))
                  ? {
                      display: `Their service ${eligibilityInformation.subjectEligibilityType === 'Ex-serving' ? 'was' : 'is'}`,
                      value: eligibilityInformation.subjectServiceType,
                    }
                  : null,
            };
            break;
          case AcceptableThisEligibilityType.WORK_CONNECTION:
            eligibilityObject = {
              ...eligibilityObject,
              subjectEligibilityType: !isEmptyString(eligibilityInformation.subjectEligibilityType)
                ? {
                    display: 'I/my partner worked for',
                    value: eligibilityInformation.subjectEligibilityType,
                  }
                : null,
              subjectDepartmentOrCompanyName: {
                display: `${eligibilityInformation.subjectEligibilityType.toLowerCase() === 'contractor' ? 'Company' : 'Department'} name`,
                value: eligibilityInformation.subjectDepartmentOrCompanyName,
              },
              workConnectionName: {
                display: 'Name of person with work connection',
                value: eligibilityInformation.workConnectionName,
              },
            };
            break;

          default:
            return eligibilityObject;
        }

        Object.entries(eligibilityObject).map((value) => {
          if (Array.isArray(value) && value.length > 1 && value[1]) {
            let valueDiff;
            let eligibiltySubjectDiff;
            let sercatFileNamesDiff = '';

            if (eligibilityInformationDiff.eligibilityType === FormFieldUpdateStatus.UPDATED) {
              valueDiff = FormFieldUpdateStatus.UPDATED;
              eligibiltySubjectDiff = FormFieldUpdateStatus.UPDATED;
              sercatFileNamesDiff = FormFieldUpdateStatus.UPDATED;
            } else {
              // because "sercatFileNames" is an array, we make the diff value based on one of values from array
              if (Array.isArray(eligibilityInformationDiff[value[0]])) {
                // eslint-disable-next-line no-nested-ternary
                valueDiff = eligibilityInformationDiff[value[0]].find((file) => {
                  return file === FormFieldUpdateStatus.UPDATED;
                })
                  ? FormFieldUpdateStatus.UPDATED
                  : eligibilityInformationDiff[value[0]].find((file) => {
                      return file === FormFieldUpdateStatus.REMOVED;
                    })
                  ? FormFieldUpdateStatus.REMOVED
                  : FormFieldUpdateStatus.NO_CHANGE;
              } else {
                valueDiff = eligibilityInformationDiff[value[0]];
              }

              eligibiltySubjectDiff =
                value[0] === 'eligibilitySubject' && eligibilityInformationDiff?.eligibilityType === FormFieldUpdateStatus.UPDATED ? FormFieldUpdateStatus.UPDATED : valueDiff;
              sercatFileNamesDiff =
                eligibilityInformationDiff?.sercatFileNames && eligibilityInformationDiff?.sercatFileNames[0] === FormFieldUpdateStatus.UPDATED
                  ? FormFieldUpdateStatus.UPDATED
                  : valueDiff;
            }

            return eligibilityValues.push({
              title: value[1].display,
              value: value[1].value,
              diff: value[0] === 'sercatFileNames' ? sercatFileNamesDiff : eligibiltySubjectDiff,
            });
          }
          return true;
        });

        eligibilityValues.push({
          title: getDeclarationLabel(eligibilityInformation?.subjectEligibilityType) || 'I declare the details that I have provided are true and correct',
          value: eligibilityInformation.declarationIsConfirmed,
          type: 'checkbox',
        });

        eligibilityInformationTile = {
          title: 'Eligibility information',
          values: eligibilityValues,
          infoText: this.refData.eligibilitySummaryInformationText.informationText, // Support copy text
        };
      }

      return eligibilityInformationTile;
    },

    getVeteranCardInformationSectionData(veteranCardInformationDiff) {
      const veteranCardInformation = this.memberDetails.veteranCard;
      const showVeteranCard = showVeteranCardSection(this.isDependant, this.memberDetails?.eligibility?.eligibilityType);
      let veteranCardInformationTile = {};

      if (showVeteranCard) {
        // Temporary array to hold each "row" of the Veteran Card information tile.
        const veteranCardLineData = [];

        // Private helper method to assist with adding items to above temporary array.
        const addVeteranCardLineInformation = (title, value, diff) => {
          veteranCardLineData.push({ title, value, diff });
        };

        addVeteranCardLineInformation('Do you hold a Veteran Card?', this.boolToYesNo(veteranCardInformation.hasVeteranCard), veteranCardInformationDiff.hasVeteranCard);

        // If there are Veteran Card details:
        if (veteranCardInformation.hasVeteranCard) {
          /**
           * TODO: Further designs will need to be implemented to determine whether multiple cards will need to be displayed
           * to the member. At the moment, we're using the first active card from the card list.
           */
          const firstActiveCardIndex = this.findFirstActiveVeteranCardIndex(veteranCardInformation.veteranCardList);
          const firstActiveCard = veteranCardInformation.veteranCardList[firstActiveCardIndex];
          const firstActiveCardDiff = veteranCardInformationDiff.veteranCardList[firstActiveCardIndex];

          const CARD_TYPE_ROW_LABEL = 'Veteran Card type';
          const ACCEPTED_CONDITIONS_ROW_LABEL = 'Accepted condition(s) covered by your card';
          const acceptedConditionsLookupData = this.filterVeteranCardWhiteOptions();

          // If the card details completely changed:
          if (firstActiveCardDiff === FormFieldUpdateStatus.UPDATED) {
            addVeteranCardLineInformation(CARD_TYPE_ROW_LABEL, firstActiveCard.cardType, FormFieldUpdateStatus.UPDATED);

            if (firstActiveCard.acceptedConditions) {
              const acceptedConditionsDescriptions = firstActiveCard.acceptedConditions.map((condition) => {
                return acceptedConditionsLookupData[condition?.code]; // Returns the condition description.
              });
              const acceptedConditionsListString = acceptedConditionsDescriptions.join(firstActiveCard.acceptedConditions.length > 1 ? ', ' : '');
              addVeteranCardLineInformation(ACCEPTED_CONDITIONS_ROW_LABEL, acceptedConditionsListString, FormFieldUpdateStatus.UPDATED);
            }
          }
          // If there were only partial changes:
          else {
            if (!firstActiveCard || !firstActiveCardDiff) {
              return {};
            }
            addVeteranCardLineInformation(CARD_TYPE_ROW_LABEL, firstActiveCard.cardType, firstActiveCardDiff.cardType || firstActiveCardDiff);

            if (firstActiveCard.cardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD.toLowerCase()) {
              if (firstActiveCard.acceptedConditions.length > 0) {
                // Filter out any end-dated accepted conditions - they will be ignored.
                const activeAcceptedConditions = this.filterActiveAcceptedConditions(firstActiveCard);

                // If there are active accepted conditions post-filtering:
                if (activeAcceptedConditions.length > 0) {
                  // Determine whether the accepted conditions list had changed in order to assist with highlighting of the row value cell.
                  const acceptedConditionsWereUpdated = firstActiveCardDiff.acceptedConditions.some((acceptedCondition) => {
                    return (
                      // Check if a new accepted condition was added, OR
                      acceptedCondition === FormFieldUpdateStatus.UPDATED ||
                      // Check if some property of the accepted condition was updated.
                      Object.keys(acceptedCondition).some((key) => {
                        return acceptedCondition[key] === FormFieldUpdateStatus.UPDATED;
                      })
                    );
                  });
                  const fieldStatus = acceptedConditionsWereUpdated ? FormFieldUpdateStatus.UPDATED : FormFieldUpdateStatus.NO_CHANGE;

                  // Generate a comma-separated string of all the active accepted conditions for display within the row value cell.
                  const acceptedConditionsDescriptions = activeAcceptedConditions.map((condition) => {
                    return acceptedConditionsLookupData[condition?.code]; // Returns the condition description.
                  });
                  const acceptedConditionsListString = acceptedConditionsDescriptions.join(activeAcceptedConditions.length > 1 ? ', ' : '');
                  addVeteranCardLineInformation(ACCEPTED_CONDITIONS_ROW_LABEL, acceptedConditionsListString, fieldStatus);
                }
                // If there are no active accepted conditions post-filtering:
                else {
                  addVeteranCardLineInformation(ACCEPTED_CONDITIONS_ROW_LABEL, '', FormFieldUpdateStatus.REMOVED);
                }
              }
            }
          }
        }

        veteranCardInformationTile = {
          title: 'Veteran Card information',
          values: veteranCardLineData,
          infoText: this.refData.veteranCardHelpText.informationText, // Support copy text
        };
      }

      return veteranCardInformationTile;
    },

    getConcessionDeclarationSectionData(concessionDeclarationDiff) {
      const concessionDeclaration = this.memberDetails.concessionCard;
      const showConcessionCard =
        showConcessionCardSection(
          this.memberDetails?.memberInformation?.residentialAddress?.state,
          this.memberDetails?.memberInformation?.dateOfBirth,
          this.memberDetails?.membershipSummary?.extrasOnly,
        ) && !this.isDependant;
      const concessionCardListDiff = concessionDeclaration?.cardList;
      const initialActiveCardList = concessionDeclaration?.initialActiveCardList || [];
      let concessionDeclarationTile = {};

      // Checks whether show the Concession Card section or no
      if (showConcessionCard) {
        const concessionCards = [];
        concessionCards.push({
          title: 'Do you hold a concession card?',
          value: this.boolToYesNo(concessionDeclaration.hasConcessionCard),
          diff: concessionDeclarationDiff.hasConcessionCard,
        });

        if (this.concessionCardDeclarationStatus) {
          if (concessionCardListDiff && concessionCardListDiff.length > 0) {
            [...concessionCardListDiff, ...initialActiveCardList.slice(concessionCardListDiff.length)].forEach((card, index) => {
              const cardTypeObj = {
                title: 'Which concession card do you hold?',
                value: this.refData?.concessionCardOptions?.find((option) => {
                  return option.returnValue === card.cardType;
                })?.display,
              };
              const cardNumberObj = {
                title: 'Card number',
                value: card.cardNumber,
              };
              const expiryDateObj = {
                title: 'Expiry date',
                value: card.expiryDate,
              };

              if (concessionCardListDiff[index]) {
                const valueDiff = concessionDeclarationDiff?.cardList[index];
                const isRemoved = valueDiff === FormFieldUpdateStatus.REMOVED;

                concessionCards.push(
                  ...[
                    {
                      ...cardTypeObj,
                      diff: isRemoved || valueDiff.cardType || FormFieldUpdateStatus.UPDATED,
                    },
                    {
                      ...cardNumberObj,
                      diff: isRemoved || valueDiff.cardNumber || FormFieldUpdateStatus.UPDATED,
                    },
                    {
                      ...expiryDateObj,
                      diff: isRemoved || valueDiff.expiryDate || FormFieldUpdateStatus.UPDATED,
                    },
                  ],
                );
              } else {
                /**
                 * if is removed.
                 * NOTE: to follow the pattern, setting all values to be empty string
                 */
                concessionCards.push(
                  ...[
                    {
                      ...cardTypeObj,
                      value: '',
                      diff: concessionDeclarationDiff?.cardList[index],
                    },
                    {
                      ...cardNumberObj,
                      value: '',
                      diff: concessionDeclarationDiff?.cardList[index],
                    },
                    {
                      ...expiryDateObj,
                      value: '',
                      diff: concessionDeclarationDiff?.cardList[index],
                    },
                  ],
                );
              }
            });
          }
        }

        concessionDeclarationTile = {
          title: 'Concession information',
          values: concessionCards,
          infoText: this.showHasConcessionCardHelpText
            ? this.refData.concessionCardAdditionalFieldsHelpText.informationText
            : this.refData.concessionCardWithoutAdditionalFieldsHelpText.informationText, // Support copy text
        };
      }

      return concessionDeclarationTile;
    },
  },
  computed: {
    summaryContent() {
      this.mapFormToSubmissionPayload();

      const memberDetails = cloneDeep(this.memberDetails);
      const diff = this.diffUpdatedValues(this.initialData, memberDetails);

      const memberInformationTile = this.getMemberInformationSectionData(diff.memberInformation);
      const contactInformationTile = this.getContactInformationSectionData(diff.contactInformation);
      const privacyAndAccessTile = this.getPrivacyAndAccessSectionData(diff.privacyAccess);
      const studentDeclarationTile = this.getStudentDeclarationSectionData(diff.studentDeclaration);
      const eligibilityInformationTile = this.getEligibilityInformationSectionData(diff.eligibility);
      const veteranCardInformationTile = this.getVeteranCardInformationSectionData(diff.veteranCard);
      const concessionDeclarationTile = this.getConcessionDeclarationSectionData(diff.concessionCard);

      let tiles = [
        memberInformationTile,
        contactInformationTile,
        privacyAndAccessTile,
        studentDeclarationTile,
        eligibilityInformationTile,
        veteranCardInformationTile,
        concessionDeclarationTile,
      ];

      tiles = tiles.filter((tile) => {
        return !isEmpty(tile);
      });

      return tiles;
    },

    iCanJoinDisplayText() {
      const selectedEligibility = this.refData.needsAnalysisEligibilityOptions.find((option) => {
        return option.returnValue === this.thisEligibilityType;
      });

      return selectedEligibility ? selectedEligibility.display : '';
    },

    isServing() {
      return this.thisEligibilityType === AcceptableThisEligibilityType.SERVING;
    },

    isContractorOrDefencePublicService() {
      return ['contractor', 'defence public service'].includes(this.memberDetails.eligibility.subjectEligibilityType.toLowerCase());
    },

    eligibilitySubjectLabel() {
      switch (this.thisEligibilityType) {
        case AcceptableThisEligibilityType.SERVING:
          return 'Who is currently serving?';

        case AcceptableThisEligibilityType.EX_SERVING:
          return 'Who served?';

        case AcceptableThisEligibilityType.WORK_CONNECTION:
          return 'Who had/has a work connection to the Defence or a Defence –related government department?';

        case AcceptableThisEligibilityType.RELATED:
          return 'Who is related to the person who serves/served?';

        default:
          return '';
      }
    },

    thisEligibilityType() {
      const eligibilityType = this.memberDetails.eligibility?.eligibilityType;
      if (EligibilityTypes.SERVING.includes(eligibilityType)) {
        return AcceptableThisEligibilityType.SERVING;
      }

      if (EligibilityTypes.EX_SERVING.includes(eligibilityType)) {
        return AcceptableThisEligibilityType.EX_SERVING;
      }

      if (EligibilityTypes.RELATED.includes(eligibilityType)) {
        return AcceptableThisEligibilityType.RELATED;
      }

      if (EligibilityTypes.WORK_CONNECTION.includes(eligibilityType)) {
        return AcceptableThisEligibilityType.WORK_CONNECTION;
      }

      return '';
    },

    mapPreferredCommunicationMethodDisplayText() {
      const pcmValue = this.memberDetails.contactInformation.preferredCommunicationMethod;

      return (
        this.refData.preferredCommunicationOptions.find((obj) => {
          return obj.returnValue.localeCompare(pcmValue, 'en', { sensitivity: 'accent' }) === 0;
          // return obj.returnValue.toUpperCase() === pcmValue.toUpperCase();
        })?.display || ''
      );
    },
    showPremiumImpactAlert() {
      // Premium Alert should only be shown if one or more of the following conditions are met:
      //  1. Changing residential address state (and post code)
      //  2. Adding or removing Veterans card
      //  3. Adding or removing a non-student dependent which changes policy scale
      //  4. Changing to or from 'Ex-Serving' eligibility and adding discharge date < 365 days
      //  5. Changing to or from 'Currently Serving' eligibility

      // Scenario 1.
      const residentialAddressChanged = this.initialData.memberInformation.residentialAddress.state !== this.memberDetails.memberInformation.residentialAddress.state;

      // Scenario 2.
      const veteranCardChanged = this.initialData.veteranCard.hasVeteranCard !== this.memberDetails.veteranCard.hasVeteranCard;

      // Scenario 3
      const scaleChanged = this.scaleChange;

      // Scenario 4.
      const dischargeDate = this.memberDetails.eligibility?.dischargeDate;
      const recentlyDischarged = dischargeDate && differenceInDays(new Date(), new Date(dischargeDate)) < 365;
      const eligibilityExServingChanged =
        // Initially Ex-Serving -> New Eligibility
        (EligibilityTypes.EX_SERVING.includes(this.initialData.eligibility?.eligibilityType) &&
          !EligibilityTypes.EX_SERVING.includes(this.memberDetails.eligibility?.eligibilityType)) ||
        // Old Eligibility -> Ex-Serving w/discharge date < 365 days
        (!EligibilityTypes.EX_SERVING.includes(this.initialData.eligibility?.eligibilityType) &&
          EligibilityTypes.EX_SERVING.includes(this.memberDetails.eligibility?.eligibilityType) &&
          recentlyDischarged);

      // Scenario 5.
      const eligibilityCurrentlyServingChanged =
        // Currently Serving -> New Eligibility
        (EligibilityTypes.SERVING.includes(this.initialData.eligibility?.eligibilityType) && !EligibilityTypes.SERVING.includes(this.memberDetails.eligibility?.eligibilityType)) ||
        // Old Eligibility -> Currently Serving
        (!EligibilityTypes.SERVING.includes(this.initialData.eligibility?.eligibilityType) && EligibilityTypes.SERVING.includes(this.memberDetails.eligibility?.eligibilityType));

      return eligibilityCurrentlyServingChanged || veteranCardChanged || eligibilityExServingChanged || residentialAddressChanged || scaleChanged;
    },
  },
};
</script>

<style lang="scss" scoped>
.sectionTitle {
  :deep(.item1) {
    label {
      border-top: 1px solid $GREY_700;
      border-bottom: 1px solid $GREY_700;
      padding: 10px 20px;
      margin: 0px;
      font-weight: $BOLD;
    }
  }

  @include tablet-and-above {
    margin-bottom: 30px;
  }

  @include mobile {
    margin-bottom: 25px;

    :deep(.item3) {
      @include grid-position(0, 4);
      margin-bottom: 30px;
    }
  }
}

.table {
  .item {
    display: flex;
    padding: 5px 20px;

    .section-row {
      display: flex;
      flex-grow: 1;
      overflow-wrap: break-word;
      gap: 40px;

      > * {
        width: 50%;
        word-break: break-word;
      }
    }

    .bold {
      font-weight: $BOLD;
    }

    .card-spacing {
      margin-top: 10px;
    }
  }
}

.form-field {
  :deep(.helper-text) {
    p {
      margin-bottom: 0px;

      @include tablet-and-above {
        margin-top: 0px;
      }
    }

    @include tablet-and-below {
      margin-top: 10px;
      margin-bottom: 0px;
    }
  }
}

.highlightUpdated {
  background-color: $PRIMARY_GREEN_TINT;
}

.highlightRemoved {
  background-color: $CRIMSON_TINT;
}

.highlightUpdatedWithPadding {
  padding-top: 10px !important;
}

.checkDisabled {
  color: $DISABLED_BTN_TEXT_COLOR;
}
</style>
